'use client';

import React from 'react';
import Link from 'next/link';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  Sparkles,
  Target,
  Mail,
  Clock,
  BarChart3,
  Users,
  Globe,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';
import { PRICING_TIERS, type PricingTierId } from '@/types';

export default function FeaturesContent() {
  const { t } = useTranslation();
  const { ref: heroRef, isVisible: heroVisible } = useIntersectionObserver();
  const { ref: gridRef, isVisible: gridVisible } = useIntersectionObserver();
  const { ref: comparisonRef, isVisible: comparisonVisible } = useIntersectionObserver();
  const { ref: ctaRef, isVisible: ctaVisible } = useIntersectionObserver();

  const features = [
    {
      icon: <Sparkles className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.features.grid.items.aiDiscovery.title'),
      description: t('pageContent.features.grid.items.aiDiscovery.description'),
    },
    {
      icon: <Target className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.features.grid.items.smartMatching.title'),
      description: t('pageContent.features.grid.items.smartMatching.description'),
    },
    {
      icon: <Mail className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.features.grid.items.personalizedOutreach.title'),
      description: t('pageContent.features.grid.items.personalizedOutreach.description'),
    },
    {
      icon: <Clock className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.features.grid.items.monthlyAutomation.title'),
      description: t('pageContent.features.grid.items.monthlyAutomation.description'),
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.features.grid.items.interestTracking.title'),
      description: t('pageContent.features.grid.items.interestTracking.description'),
    },
    {
      icon: <Users className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.features.grid.items.dashboard.title'),
      description: t('pageContent.features.grid.items.dashboard.description'),
    },
    {
      icon: <Globe className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.features.grid.items.globalNetwork.title'),
      description: t('pageContent.features.grid.items.globalNetwork.description'),
    },
  ];

  // Prices and investor counts are derived from PRICING_TIERS — types/index.ts is
  // the source of truth — so this block cannot drift from real pricing again.
  const tierFor = (id: PricingTierId) => PRICING_TIERS.find((tier) => tier.id === id);
  const monthlyPrice = (id: PricingTierId) => {
    const cents = tierFor(id)?.priceMonthly;
    return cents === undefined ? '' : `$${Math.round(cents / 100)}`;
  };
  const vcCount = (id: PricingTierId) => tierFor(id)?.vcCount ?? 0;

  // The only real difference between plans is monthly volume, plus priority
  // support on Scale. Personalized AI emails and multi-industry matching are not
  // tier-gated anywhere in the code, and every tier reaches the same investor
  // pool — so those lines read identically across all three plans here.
  // Shared across all three plans, so it is built once. The volume line is the
  // only per-plan value; everything else is identical because nothing else is
  // tier-gated in code.
  const sharedFeatures = [
    t('pageContent.features.comparison.aiMatching'),
    t('pageContent.features.comparison.personalizedEmails'),
    t('pageContent.features.comparison.multiIndustry'),
    t('pageContent.features.comparison.interestTracking'),
  ];
  const planFeatures = (id: PricingTierId) => [
    t('pageContent.features.comparison.vcsPerMonth', { count: vcCount(id) }),
    ...sharedFeatures,
  ];

  const plans = [
    {
      name: t('pageContent.features.comparison.starter'),
      price: monthlyPrice('starter'),
      features: planFeatures('starter'),
    },
    {
      name: t('pageContent.features.comparison.professional'),
      price: monthlyPrice('professional'),
      highlighted: true,
      features: planFeatures('professional'),
    },
    {
      name: t('pageContent.features.comparison.scale'),
      price: monthlyPrice('scale'),
      features: planFeatures('scale'),
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container">
          <div ref={heroRef} className={`max-w-3xl mx-auto text-center fade-in-section ${heroVisible ? 'is-visible' : ''}`}>
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.features.hero.tagline')}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('pageContent.features.hero.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('pageContent.features.hero.subtitle')}
            </p>
            {/* Anchor styled as a button rather than an anchor wrapping one —
                an anchor containing a button is invalid HTML. cn() reproduces
                what the Button component did internally, so a className that
                overrides a variant utility wins by twMerge rather than by
                stylesheet order. */}
            <Link
              href="/#upload-section"
              className={cn(buttonVariants({ size: 'lg', className: 'px-8 hover-scale' }))}
            >
              {t('pageContent.features.cta.uploadPitch')} <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div ref={gridRef} className={`text-center mb-16 fade-in-section ${gridVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('pageContent.features.grid.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow hover-lift fade-in-section ${gridVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-primary-50 rounded-xl flex items-center justify-center mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container">
          <div ref={comparisonRef} className={`text-center mb-16 fade-in-section ${comparisonVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('pageContent.features.comparison.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('pageContent.features.comparison.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-sm border p-8 text-center hover-lift fade-in-section ${comparisonVisible ? 'is-visible' : ''} ${
                  plan.highlighted
                    ? 'border-primary-500 ring-2 ring-primary-500 ring-opacity-20 relative'
                    : 'border-gray-100'
                }`}
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white px-4 py-1 rounded-full text-xs font-semibold">
                    Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500 ml-1">/ {t('pageContent.features.comparison.perMonth')}</span>
                </div>
                <ul className="space-y-3 mb-8 text-left">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary-500 flex-shrink-0 mr-2 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  href="/pricing"
                  className={cn(
                    buttonVariants({
                      variant: plan.highlighted ? 'default' : 'outline',
                      fullWidth: true,
                      className: 'hover-scale',
                    })
                  )}
                >
                  {t('pageContent.features.cta.viewPricing')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container text-center">
          <div ref={ctaRef} className={`fade-in-section ${ctaVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('pageContent.features.cta.title')}
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              {t('pageContent.features.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/pricing"
                className={cn(buttonVariants({ variant: 'secondary', size: 'lg', className: 'px-8 hover-scale' }))}
              >
                {t('pageContent.features.cta.viewPricing')}
              </Link>
              <Link
                href="/#upload-section"
                className={cn(buttonVariants({ variant: 'outline', size: 'lg', className: 'px-8 hover-scale border-white text-white hover:bg-white/10' }))}
              >
                {t('pageContent.features.cta.uploadPitch')} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
